import { Button, Drawer, Space, Select, Spin, Empty, Divider, Typography, Table, message, InputNumber, Input } from "antd"
import { FileOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { useState, useEffect } from "react"
import { collection, onSnapshot } from "firebase/firestore"
import { db } from "../../firebase/config"
import { Document, Page, Text as PDFText, View, Image as PDFImage, StyleSheet, pdf } from "@react-pdf/renderer"

const { Text } = Typography

const pdfStyles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: "Helvetica",
    fontSize: 11,
    lineHeight: 1.4,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingBottom: 20,
  },
  logo: {
    maxWidth: 150,
    maxHeight: 60,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 20,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    marginBottom: 8,
  },
  row: {
    flexDirection: "row",
    marginBottom: 6,
  },
  label: {
    fontWeight: "bold",
    width: 120,
  },
  value: {
    flex: 1,
  },
  table: {
    display: "table",
    width: "100%",
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    marginBottom: 20,
  },
  tableRow: {
    margin: "auto",
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
  },
  tableCell: {
    margin: "auto",
    padding: 8,
    flexGrow: 1,
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
  },
  footerSection: {
    marginTop: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
})

function PackingListPDF({ order, manufacturer, products = [] }) {
  const orderDate = order?.created?.seconds
    ? new Date(order.created.seconds * 1000).toLocaleDateString("lv-LV")
    : "—"
  const clientName = order?.client?.client || "—"
  const clientAddress = order?.deliveryAddress || "—"

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        {/* Header */}
        <View style={pdfStyles.header}>
          <View>
            <PDFText style={pdfStyles.title}>PACKING LIST</PDFText>
          </View>
          {manufacturer?.croppedLogo && (
            <PDFImage src={manufacturer.croppedLogo} style={pdfStyles.logo} />
          )}
        </View>

        {/* Sender and Recipient */}
        <View style={{ flexDirection: "row", marginBottom: 30 }}>
          {/* Recipient */}
          <View style={{ flex: 1, marginRight: 20 }}>
            <PDFText style={pdfStyles.sectionTitle}>DELIVER TO:</PDFText>
            <View style={pdfStyles.section}>
              <PDFText>{clientName}</PDFText>
              <PDFText>{clientAddress}</PDFText>
            </View>
          </View>

          {/* Sender */}
          <View style={{ flex: 1 }}>
            <PDFText style={pdfStyles.sectionTitle}>FROM:</PDFText>
            <View style={pdfStyles.section}>
              <PDFText>{manufacturer?.company || "—"}</PDFText>
              <PDFText>{manufacturer?.deliveryAddress || "—"}</PDFText>
              <PDFText>
                {manufacturer?.deliveryPC} {manufacturer?.deliveryCity}
              </PDFText>
            </View>
          </View>
        </View>

        {/* Order Details */}
        <View style={pdfStyles.section}>
          <View style={pdfStyles.row}>
            <PDFText style={pdfStyles.label}>Order No.:</PDFText>
            <PDFText style={pdfStyles.value}>{order?.orderNumber || "—"}</PDFText>
          </View>
          <View style={pdfStyles.row}>
            <PDFText style={pdfStyles.label}>Order Date:</PDFText>
            <PDFText style={pdfStyles.value}>{orderDate}</PDFText>
          </View>
          <View style={pdfStyles.row}>
            <PDFText style={pdfStyles.label}>Delivery Date:</PDFText>
            <PDFText style={pdfStyles.value}>
              {new Date().toLocaleDateString("lv-LV")}
            </PDFText>
          </View>
        </View>

        {/* Items Table */}
        <View style={pdfStyles.table}>
          <View style={[pdfStyles.tableRow, pdfStyles.tableHeader]}>
            <View style={[pdfStyles.tableCell, { width: "12%" }]}>
              <PDFText>Item</PDFText>
            </View>
            <View style={[pdfStyles.tableCell, { width: "36%" }]}>
              <PDFText>Description</PDFText>
            </View>
            <View style={[pdfStyles.tableCell, { width: "25%" }]}>
              <PDFText>Type</PDFText>
            </View>
            <View style={[pdfStyles.tableCell, { width: "17%" }]}>
              <PDFText>Quantity</PDFText>
            </View>
          </View>

          {products.map((product, index) => (
            <View style={pdfStyles.tableRow} key={product.id}>
              <View style={[pdfStyles.tableCell, { width: "12%" }]}>
                <PDFText>{index + 1}</PDFText>
              </View>
              <View style={[pdfStyles.tableCell, { width: "36%" }]}>
                <PDFText>{product.description || "—"}</PDFText>
              </View>
              <View style={[pdfStyles.tableCell, { width: "25%" }]}>
                <PDFText>{product.type || "—"}</PDFText>
              </View>
              <View style={[pdfStyles.tableCell, { width: "17%" }]}>
                <PDFText>{product.quantity || "—"}</PDFText>
              </View>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={pdfStyles.footerSection}>
          <View>
            <PDFText style={pdfStyles.sectionTitle}>Signature</PDFText>
            <View
              style={{
                borderTopWidth: 1,
                borderTopColor: "#000",
                width: 150,
                marginTop: 30,
              }}
            />
          </View>
          <View>
            <PDFText style={pdfStyles.sectionTitle}>Date</PDFText>
            <PDFText>_______________________</PDFText>
          </View>
        </View>
      </Page>
    </Document>
  )
}

export default function PackingListDrawer({ orderData, disabled = true, buttonText = "Packing List" }) {
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [manufacturers, setManufacturers] = useState([])
  const [selectedManufacturer, setSelectedManufacturer] = useState(null)
  const [manufacturersLoading, setManufacturersLoading] = useState(true)
  const [products, setProducts] = useState([])

  // Initialize products from order data
  useEffect(() => {
    if (orderData && drawerVisible) {
      const isTape = orderData?.selectedProduct?.machine === "Tape"
      setProducts([
        {
          id: `product-${Date.now()}`,
          description: orderData?.selectedProduct?.product?.productName || "—",
          quantity: isTape ? orderData.rollCount || 0 : orderData.amountCreated || orderData.labelCount || 0,
          type: isTape ? "Rolls" : "Pieces",
        },
      ])
    }
  }, [orderData, drawerVisible])

  const addProduct = () => {
    setProducts([
      ...products,
      {
        id: `product-${Date.now()}`,
        description: "",
        quantity: 0,
        type: "Pieces",
      },
    ])
  }

  const removeProduct = (id) => {
    setProducts(products.filter((p) => p.id !== id))
  }

  const updateProduct = (id, field, value) => {
    setProducts(products.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  // Fetch manufacturers
  useEffect(() => {
    setManufacturersLoading(true)
    const manufacturersRef = collection(db, "manufacturers")

    const unsubscribe = onSnapshot(manufacturersRef, (snapshot) => {
      const mfgList = snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
      setManufacturers(mfgList)
      setManufacturersLoading(false)
    })

    return () => unsubscribe()
  }, [])

  const handleDrawerClose = () => {
    setDrawerVisible(false)
    setSelectedManufacturer(null)
  }

  const handleGeneratePacking = async () => {
    if (!selectedManufacturer || !orderData || products.length === 0) {
      message.error("Please select a manufacturer and add products")
      return
    }

    try {
      const doc = (
        <PackingListPDF
          order={orderData}
          manufacturer={mfgDetails}
          products={products}
        />
      )
      const blob = await pdf(doc).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `packing-list-${orderData.orderNumber || "order"}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      message.success("Packing list downloaded successfully")
      handleDrawerClose()
    } catch (err) {
      message.error("Error generating packing list: " + err.message)
    }
  }

  // Get selected manufacturer details
  const mfgDetails = manufacturers.find((m) => m.id === selectedManufacturer)

  // Table columns for packing list items
  const packingListColumns = [
    {
      title: "Item No.",
      dataIndex: "itemNo",
      align: "center",
      width: "7%",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Description",
      dataIndex: "description",
      align: "left",
      width: "44%",
      render: (text, record) => (
        <Input
          value={text}
          onChange={(e) => updateProduct(record.id, "description", e.target.value)}
          placeholder="Product name"
          size="small"
          style={{ fontSize: 11, padding: "2px 4px" }}
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
      width: "14%",
      render: (type, record) => (
        <Select
          value={type || "Pieces"}
          onChange={(value) => updateProduct(record.id, "type", value)}
          size="small"
          style={{ width: "100%", fontSize: 11 }}
          options={[
            { value: "Thousands", label: "Thousands" },
            { value: "Pieces", label: "Pieces" },
            { value: "Boxes", label: "Boxes" },
            { value: "Rolls", label: "Rolls" },
          ]}
        />
      ),
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      align: "center",
      width: "18%",
      render: (quantity, record) => (
        <InputNumber
          min={0}
          value={quantity || 0}
          onChange={(value) => updateProduct(record.id, "quantity", value)}
          size="small"
          style={{ width: "80px" }}
        />
      ),
    },
    {
      title: "Action",
      align: "center",
      width: "7%",
      render: (_, record) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => removeProduct(record.id)}
        />
      ),
    },
  ]

  return (
    <>
      <Button
        type="primary"
        icon={<FileOutlined />}
        onClick={() => setDrawerVisible(true)}
        disabled={disabled}>
        {buttonText}
      </Button>

      <Drawer
        title="Generate Packing List"
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width={750}>
        <Space direction="vertical" style={{ width: "100%" }} size="large">
          {/* Manufacturer Selection Section */}
          <Spin spinning={manufacturersLoading}>
            {manufacturers.length === 0 ? (
              <Empty description="No manufacturers found" />
            ) : (
              <div>
                <Text strong>Select Sender (Manufacturer)</Text>
                <Select
                  style={{ width: "100%", marginTop: 8 }}
                  placeholder="Choose a manufacturer"
                  value={selectedManufacturer}
                  onChange={setSelectedManufacturer}
                  options={manufacturers.map((mfg) => ({
                    value: mfg.id,
                    label: mfg.company,
                  }))}
                />
              </div>
            )}
          </Spin>

          <Divider />

          {/* Product Lines Section */}
          {products.length > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text strong>Order Products</Text>
                <Button
                  type="dashed"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={addProduct}>
                  Add Product
                </Button>
              </div>
              <Table
                size="small"
                columns={packingListColumns}
                dataSource={products}
                pagination={false}
                bordered
                rowKey="id"
                style={{ fontSize: 12 }}
              />
              <Divider />
            </>
          )}

          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleDrawerClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleGeneratePacking}
              disabled={!selectedManufacturer || products.length === 0}>
              Generate
            </Button>
          </Space>
        </Space>
      </Drawer>
    </>
  )
}
